import { usePostHog } from "@posthog/react";
import { Button, Col, Layout, Row, Space, Tabs, Typography, message } from "antd";
import TabPane from "antd/es/tabs/TabPane";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import SwipeHandUp from "../../../assets/gif/finger-animation.gif";
import ActiveCollectionIcon from "../../../assets/icons/active-collection.svg";
import ActiveGridIcon from "../../../assets/icons/active-grid.svg";
import BookmarkFilledOtherProfileIcon from "../../../assets/icons/bookmark-filled-other-profile.svg";
import BookmarkOtherProfileIcon from "../../../assets/icons/bookmark-other-profile.svg";
import ChatIcon from "../../../assets/icons/chat-icon.svg";
import ChevronBackOtherProfileIcon from "../../../assets/icons/chevron-back-other-profile.svg";
import CollectionIcon from "../../../assets/icons/collection.svg";
import GridIcon from "../../../assets/icons/grid.svg";
import LocationIcon from "../../../assets/icons/location.svg";
import ShareOtherProfileIcon from "../../../assets/icons/share-other-profile.svg";
import VerifyIcon from "../../../assets/icons/verify.svg";
import AvatarComponent from "../../../components/AvatarComponent";
import CollectionGridComponent from "../../../components/Collections/CollectionGridComponent";
import PresentationVideoButton from "../../../components/discover/common/PresentationVideoButton";
import FanProfileTrainingOverlayComponent from "../../../components/FanProfileTrainingOverlayComponent";
import FanReportCreatorModalComponent from "../../../components/FanReportCreatorModalComponent";
import FooterComponent from "../../../components/FooterComponent";
import FreeChatButtonComponent from "../../../components/FreeChatButtonComponent/FreeChatButtonComponent";
import ReportModal from "../../../components/ReportModal";
import ShareModalComponent from "../../../components/ShareModalComponent";
import ThreeDotsComponent from "../../../components/ThreeDotsComponent";
import { CREATOR, FOOTER_IDS, MAX_NAME_LENGTH } from "../../../helpers/constant";
import useProfileCustomPageInfos from "../../../hooks/global/useProfileCustomPageInfos";
import useFetchOtherUserInfos from "../../../hooks/userHooks/useFetchOtherUserInfos";
import useFetchUserSettings from "../../../hooks/userHooks/useFetchUserSettings";
import { selectConversation } from "../../../redux/messages/messagesSlice";
import { createReaction } from "../../../redux/reactions/reactionsSlice";
import { AppDispatch } from "../../../redux/store";
import { updateUserReaction, updateUserSettingsStatus } from "../../../redux/users/usersSlice";
import { messageService, reactionService } from "../../../services";
import { SUBSCRIPTION_STATUS } from "../../../utls/constants";
import FeedGridComponent from "./FeedGridComponent";
import ProfileEvent from "./ProfileEvent";

type Props = {
  username: string;
  currentUserRole: string;
  currentUserId: string;
  handleNavigate: () => void;
};

const OtherUserProfilePage: React.FC<Props> = ({ username, currentUserRole, currentUserId, handleNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const posthog = usePostHog();
  const { otherUser: user, error } = useFetchOtherUserInfos(username);
  const { currentUserSettings } = useFetchUserSettings();
  useProfileCustomPageInfos({ username, name: user?.infos?.name, avatarPath: user?.infos?.avatar, t });
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState<boolean>(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [fanModalStep, setFanModalStep] = useState(1);
  const iconSize = user && user.infos.name.length > MAX_NAME_LENGTH ? "16px" : "22px";
  const [isFollowed, setIsFollowed] = useState<boolean>(user?.infos.isFollowed || false);

  useEffect(() => {
    if (currentUserSettings) {
      setShowOverlay(!currentUserSettings?.hasSeenFanProfileModal || false);
    }
  }, [currentUserSettings?.hasSeenFanProfileModal]);

  useEffect(() => {
    if (user && activeTab === null) {
      const savedTab = localStorage.getItem("activeProfileTab");
      setActiveTab(savedTab || user.userSettings?.defaultPage || "feeds");
    }
  }, [user, activeTab]);

  useEffect(() => {
    if (user?.infos?.isFollowed !== undefined) {
      setIsFollowed(user.infos.isFollowed);
    }
  }, [user?.infos?.isFollowed]);

  // Track profile view
  useEffect(() => {
    if (user) {
      posthog.capture("profile_viewed", {
        username: username,
        creator_name: user.infos.name,
        creator_id: user.id,
        has_discovers: user.userSettings?.hasDiscovers,
        verified: user.infos.verifiedAccount,
        total_feeds: user.infos.stats?.totalFeeds,
        total_collections: user.infos.stats?.totalCollections,
        viewer_role: currentUserRole,
      });
    }
  }, [user]);

  useEffect(() => {
    if (user && !user.infos?.isSubscribed && currentUserRole !== CREATOR) {
      const payload = {
        action: "interested",
        objectId: user.id,
        objectType: "creator",
      };
      dispatch(createReaction(payload));
    }
  }, [user?.infos?.isSubscribed]);

  const followOrUnFollow = (creatorId: string) => {
    const payload = {
      action: "follow",
      objectId: creatorId,
      objectType: "creator",
    };
    if (user?.infos.isFollowed) {
      reactionService.deactivatedReaction(payload).then((result) => {
        dispatch(
          updateUserReaction({
            id: creatorId,
            isFollowed: false,
          })
        );
        message.success(t("unfollow-success", { name: user?.infos.name }));
      });
    } else {
      reactionService.createReaction(payload).then((result) => {
        dispatch(
          updateUserReaction({
            id: creatorId,
            isFollowed: true,
          })
        );
        message.success(t("follow-success", { name: user?.infos.name }));
      });
    }
  };

  const onSubscribeClick = () => {
    if (user && user?.infos?.subscriptionStatus === SUBSCRIPTION_STATUS.DEACTIVATED) {
      posthog.capture("subscribe_button_clicked", {
        username: username,
        creator_id: user?.id,
        subscription_status: user?.infos?.subscriptionStatus,
        is_subscribed: user?.infos?.isSubscribed,
      });
      navigate(`/common/subscribe/${user?.id}`);
    }
  };

  const startConversation = async () => {
    if (!user) return;

    posthog.capture("chat_initiated", {
      username: username,
      creator_id: user.id,
      chat_type: user.userSettings?.chat === "free" ? "free" : "paid",
    });

    let urlToNavigate = currentUserRole === CREATOR ? "/creator/conversation/new" : "/fan/requestMedia/new";
    const conversation = await messageService.isConversationExist({
      fanId: currentUserId,
      creatorId: user.infos._id,
    });

    dispatch(selectConversation(conversation));

    urlToNavigate = urlToNavigate.replace("new", conversation?._id);
    navigate(urlToNavigate, { replace: false });
  };

  const handleTabChange = (key: string) => {
    localStorage.setItem("activeProfileTab", key);
    setActiveTab(key);
  };

  const fanModalNextStep = () => {
    if (fanModalStep === 1) {
      setFanModalStep(2);
    } else {
      if (!currentUserSettings?.hasSeenFanProfileModal) {
        dispatch(updateUserSettingsStatus("hasSeenFanProfileModal"));
      }
    }
  };

  const goPresentationVideos = () => {
    posthog.capture("presentation_video_clicked", {
      username: username,
      source: "profile_page",
    });
    navigate(`/common/presentation/videos/${username}`);
  };

  const handleToggle = () => {
    setIsFollowed(!isFollowed);
  };

  const openOptionsModal = useCallback(() => {
    setIsOptionsModalOpen(true);
  }, []);

  const closeShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const closeReportModal = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  const closeOptionsModal = useCallback(() => {
    setIsOptionsModalOpen(false);
  }, []);

  const onReportOptionsClick = useCallback(() => {
    setIsOptionsModalOpen(false);
    setIsReportModalOpen(true);
  }, []);

  const Icon = isFollowed ? BookmarkFilledOtherProfileIcon : BookmarkOtherProfileIcon;

  return (
    <Layout className="gradient-background-profile no-scroll-horizontal">
      <Layout.Content className="content fanProfileContentWrapper background-transparent">
        <Row className="header-container">
          <img
            src={ChevronBackOtherProfileIcon}
            width={10}
            alt="arrow left"
            onClick={() => {
              handleNavigate();
            }}
          />
          <div className="header-text-other-profile" aria-hidden="true">
            @{username}
          </div>

          <div>
            {/* Hide bookmark for creator-to-creator interactions */}
            {!(currentUserRole === CREATOR) && (
              <img
                src={Icon}
                className="mr-15"
                width={16}
                alt="bookmark"
                onClick={() => {
                  posthog.capture(isFollowed ? "profile_unfollowed" : "profile_followed", {
                    username: username,
                    creator_id: user?.id || "",
                  });
                  handleToggle();
                  followOrUnFollow(user?.id || "");
                }}
              />
            )}
            <img
              className="mr-10"
              src={ShareOtherProfileIcon}
              width={20}
              alt="share"
              onClick={() => {
                posthog.capture("profile_shared", {
                  username: username,
                  creator_id: user?.id,
                });
                setIsShareModalOpen(true);
              }}
            />
          </div>
        </Row>
        <div className="fanProfileTopWrapper">
          <Row align="middle">
            <Col className="colAvatar mr-10">
              <AvatarComponent
                image={user?.infos.avatar || ""}
                size={105}
                fontSize={16}
                fontWeight={true}
                isLargeView={true}
                profileName={user?.infos?.name}
                usernameAltText={username}
              />
            </Col>
            <Col className="colDetails">
              <Row className="nameRow" align="middle">
                <h1 className={`nameTitleOtherProfile`}>{error !== "user-not-found" ? <>{user?.infos.name}</> : t(error as string)}</h1>
                {user?.infos.verifiedAccount && <img className="verifyIcon" src={VerifyIcon} width={iconSize} alt="verify" />}
              </Row>

              <Row className="mt-5 profile-button-container" align="middle">
                <div className="profile-action-buttons">
                  {/* Hide subscribe and message buttons when current user is a creator */}
                  {!(currentUserRole === CREATOR) && (
                    <>
                      {user?.userSettings?.chat === "free" ? (
                        <>
                          <FreeChatButtonComponent onClick={() => startConversation()} />

                          <Button
                            className={`subscribeBtn freeChat ${user?.infos?.subscriptionStatus === SUBSCRIPTION_STATUS?.DEACTIVATED ? "subscribeWithoutObligation" : "unsubscribe"}`}
                            onClick={() => onSubscribeClick()}
                          >
                            <span>
                              {t(
                                `${user?.infos?.subscriptionStatus === SUBSCRIPTION_STATUS?.DEACTIVATED ? (user?.infos?.isSubscribed === true ? "extend-subscription" : "subscribe") : "subscriber"}`
                              )}
                            </span>
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            className={`subscribeBtn ${user?.infos?.subscriptionStatus === SUBSCRIPTION_STATUS?.DEACTIVATED ? "subscribeWithoutObligation" : "unsubscribe"}`}
                            onClick={() => onSubscribeClick()}
                          >
                            <span>
                              {t(
                                `${user?.infos?.subscriptionStatus === SUBSCRIPTION_STATUS?.DEACTIVATED ? (user?.infos?.isSubscribed === true ? "extend-subscription" : "Subscribe-without-obligation") : "subscriber"}`
                              )}
                            </span>
                          </Button>
                          <img
                            width={43}
                            src={ChatIcon}
                            onClick={() => {
                              startConversation();
                            }}
                            alt="chat"
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              </Row>
            </Col>
          </Row>
        </div>
        <div className="labelWrapper">
          {user?.infos.city && (
            <Row className="m-5">
              <Col className="colValues location">
                <img className="locationImg mr-5" src={LocationIcon} width={16} alt="location" />
                <Typography.Text className="fanProfileValue">{user.infos.city}</Typography.Text>
              </Col>
            </Row>
          )}
          {user?.userSettings?.hasDiscovers && <PresentationVideoButton onClick={goPresentationVideos} notificationVisible={false} />}
          <Row className="mt-10">
            <div
              className="fanProfileDescriptionLabel"
              dangerouslySetInnerHTML={{
                __html: (user?.infos.description || "").replace(/\n/g, "<br />"),
              }}
            />
          </Row>

          <Row className="mt-10 max-w-80 justify-content-between" align="middle">
            <div className="leftItems">
              <Space>
                <Col
                  className="colValues"
                  onClick={() => {
                    posthog.capture("publications_tab_clicked", {
                      username: username,
                      total_feeds: user?.infos.stats?.totalFeeds || 0,
                    });
                    handleTabChange("publications");
                  }}
                >
                  <Typography.Text className="fanProfileAttributeValue">{user?.infos.stats?.totalFeeds || 0}</Typography.Text>
                  <Typography.Text className="fanProfileValue">{t("publications")}</Typography.Text>
                </Col>
              </Space>

              <Space>
                <Col
                  className="colValues"
                  onClick={() => {
                    posthog.capture("collection_tab_clicked", {
                      username: username,
                      total_collections: user?.infos.stats?.totalCollections || 0,
                    });
                    handleTabChange("collection");
                  }}
                >
                  <Typography.Text className="fanProfileAttributeValue">{user?.infos.stats?.totalCollections || 0}</Typography.Text>
                  <Typography.Text className="fanProfileValue">{t("collections")}</Typography.Text>
                </Col>
              </Space>
            </div>

            <ThreeDotsComponent onClick={openOptionsModal} />
          </Row>
        </div>

        {user && <ProfileEvent isMyProfile={false} username={username} creatorId={user?.id!} userSettings={user?.userSettings!} />}

        <Col className={`feeds-container ${showOverlay ? "overlay-visible" : ""}`}>
          {user?.infos?.name && <h2 className="exclusive-content-title">{t("exclusive-content-of", { name: user.infos.name })}</h2>}
          {!activeTab ? (
            <></>
          ) : (
            <Tabs activeKey={activeTab} onChange={showOverlay ? fanModalNextStep : handleTabChange}>
              <TabPane
                tab={
                  <div className="tab-container">
                    <img
                      src={fanModalStep === 2 && showOverlay ? ActiveGridIcon : !showOverlay && activeTab === "feeds" ? ActiveGridIcon : GridIcon}
                      width={showOverlay ? 30 : 20}
                      alt="publications icon"
                      className={`${fanModalStep !== 2 && showOverlay ? "opacity-icon" : ""}`}
                    />
                    {fanModalStep === 2 && showOverlay && <img className="swipe-icon" src={SwipeHandUp} alt="swipe up" />}
                  </div>
                }
                key="feeds"
              >
                <FeedGridComponent
                  isMyProfile={false}
                  creatorId={user?.id!}
                  subscribed={user?.infos.isSubscribed || false}
                  profileName={user?.infos?.name}
                  username={username}
                />
              </TabPane>

              <TabPane
                tab={
                  <div className="tab-container">
                    <img
                      src={
                        fanModalStep === 1 && showOverlay
                          ? ActiveCollectionIcon
                          : !showOverlay && activeTab === "collection"
                            ? ActiveCollectionIcon
                            : CollectionIcon
                      }
                      width={showOverlay ? 30 : 20}
                      className={`${fanModalStep !== 1 && showOverlay ? "opacity-icon" : ""}`}
                      alt="collections icon"
                    />
                    {fanModalStep === 1 && showOverlay && <img className="swipe-icon" src={SwipeHandUp} alt="swipe up" />}
                  </div>
                }
                key="collection"
              >
                <CollectionGridComponent
                  isMyProfile={false}
                  creatorId={user?.id}
                  username={username}
                  profileName={user?.infos?.name}
                  goToPublicationTab={() => handleTabChange("feeds")}
                />
              </TabPane>
            </Tabs>
          )}
        </Col>
        {showOverlay && <FanProfileTrainingOverlayComponent step={fanModalStep} fanModalStep={fanModalNextStep} />}
        <ShareModalComponent isOpen={isShareModalOpen} onClose={closeShareModal} id={currentUserId} username={username} />
        <FanReportCreatorModalComponent isOpen={isOptionsModalOpen} onClose={closeOptionsModal} onReportClick={onReportOptionsClick} />
        <ReportModal isOpen={isReportModalOpen} target="creator" targetId={user?.id!} onClose={closeReportModal} />
      </Layout.Content>
      <FooterComponent page={FOOTER_IDS.SEARCH} />
    </Layout>
  );
};
export default OtherUserProfilePage;
